/**
 * The rest of the formatting: numbers, percentages and quantities with their unit
 * noun. Money and dates format in `money.ts` and `dates.ts`, which is where their
 * rules live; the SPA's `useFormat()` (session 08) is a thin hook over all three.
 *
 * The package barrel is the one place everything is re-exported from, so nothing
 * here re-exports `formatMoney` or `formatDate` — two paths to the same function
 * would collide in `index.ts`.
 */
import { DomainError } from './errors.js';
import { MINUS_SIGN, formatWithMinusSign, numberFormat, pluralRules } from './intl.js';
const DEFAULT_LOCALE = 'en-US';
export function formatNumber(value, locale = DEFAULT_LOCALE, options = {}) {
    const digits = options.fractionDigits;
    if (digits !== undefined && (!Number.isSafeInteger(digits) || digits < 0 || digits > 20)) {
        throw new DomainError('ValidationFailed', 'Fraction digits are a whole number, 0 to 20.', {
            fractionDigits: String(digits),
        });
    }
    return formatWithMinusSign(numberFormat(locale, digits === undefined
        ? { maximumFractionDigits: 2 }
        : { minimumFractionDigits: digits, maximumFractionDigits: digits }), value);
}
/**
 * A fraction of one as a percentage: `0.801025641` becomes `80.1%`.
 *
 * **Not `formatPercent`, and the difference is the unit.** A rate the *engine* decides
 * is carried in basis points and is a whole number by construction (`applyBps`); a
 * ratio a *read model* computes is a fraction of one and is not — `0.801025641 × 10_000`
 * is not a safe integer, and `formatPercent` refuses it, correctly. There was no
 * formatter for the second shape at all, so three files wrote their own `× 100` beside
 * a `PER_CENT = 100` constant: `pages/studio/analytics/render.ts`,
 * `pages/studio/clients/percent.ts` and the report's `SocialSection`. This is the one
 * they now share.
 *
 * The scaling is `Intl`'s own, not a multiplication here: `style: 'percent'` takes the
 * fraction and rounds once. Multiplying first and formatting as a decimal rounds a
 * number that has already lost something — `0.385 * 100` is `38.500000000000004` — and
 * would let the signed and unsigned forms of the same figure disagree in the last
 * place, which is exactly what `sign: false` is for: a hero prints `71.0` and a
 * superscript `%` beside it, and the two have to be the same number as the `71.0%` in
 * the table under it.
 */
export function formatFraction(value, options = {}) {
    const digits = options.fractionDigits ?? 1;
    if (!Number.isSafeInteger(digits) || digits < 0 || digits > 20) {
        throw new DomainError('ValidationFailed', 'Fraction digits are a whole number, 0 to 20.', {
            fractionDigits: String(digits),
        });
    }
    if (!Number.isFinite(value)) {
        throw new DomainError('ValidationFailed', 'A fraction is a finite number — 0.801 is 80.1%.', {
            value: String(value),
        });
    }
    const formatter = numberFormat(options.locale ?? DEFAULT_LOCALE, {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
    const parts = formatter.formatToParts(value);
    return parts
        .filter((part) => options.sign !== false || part.type !== 'percentSign')
        .map((part) => (part.type === 'minusSign' ? MINUS_SIGN : part.value))
        .join('');
}
/**
 * Basis points as a percentage: 1500 is `15%`, 2500 is `25%`, 250 is `2.5%`. Rates
 * are carried as bps everywhere (`applyBps`), so this is the only place they turn
 * back into something a person reads.
 */
export function formatPercent(bps, locale = DEFAULT_LOCALE) {
    if (!Number.isSafeInteger(bps)) {
        throw new DomainError('ValidationFailed', 'Basis points are a whole number — 1500 is 15%.', {
            bps: String(bps),
        });
    }
    return formatWithMinusSign(numberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }), bps / 10_000);
}
export function formatQty(qty, unit, options = {}) {
    const locale = options.locale ?? DEFAULT_LOCALE;
    const digits = options.fractionDigits;
    const number = digits === undefined
        ? formatNumber(qty, locale)
        : formatNumber(qty, locale, { fractionDigits: digits });
    const noun = typeof unit === 'string'
        ? unit
        : pluralRules(locale).select(qty) === 'one'
            ? unit.one
            : unit.other;
    return `${number} ${noun}`;
}
/**
 * A byte count as a person reads it: `3755840000` → `3.8 GB`.
 *
 * `client/files.html` prints `3.8 GB`, `2.1 GB`, `184 MB`, `640 KB` — every one of
 * them a size the API publishes as an integer. Dividing by 1,024 in a component is
 * arithmetic on a figure, which is what `.docs/00-index.md` §5 keeps out of the SPA;
 * it is also the sort of thing three pages would each get slightly different.
 *
 * **Decimal units, not binary.** `1 KB` is 1,000 bytes here, because that is what a
 * file manager, an operating system and a client's own intuition say a 2.1 GB video
 * is. The alternative spells the same file `1.96 GiB`, which nobody has asked for.
 *
 * One decimal place from `MB` up and none below it: `640 KB` rather than `640.0 KB`,
 * `3.8 GB` rather than `4 GB`. `Intl` does the rounding, because `Math.round` is
 * banned outside `money.ts` and `dates.ts` and because the locale owns the separator.
 */
const BYTE_UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
/** Below this many of a unit, step down. Decimal, as above. */
const BYTE_STEP = 1000;
export function formatBytes(bytes, locale = DEFAULT_LOCALE) {
    if (!Number.isFinite(bytes) || bytes < 0) {
        throw new DomainError('ValidationFailed', 'A size is a whole number of bytes, or zero.', {
            bytes: String(bytes),
        });
    }
    let value = bytes;
    let unit = 0;
    while (value >= BYTE_STEP && unit < BYTE_UNITS.length - 1) {
        value /= BYTE_STEP;
        unit += 1;
    }
    /*
     * One decimal below ten, whole at ten and above — which is what every size in
     * `mockups/client/files.html` does: `1.2 MB`, `3.8 GB`, `1.4 GB`, and `42 MB`,
     * `184 MB`, `640 KB`. It is significant figures rather than a fixed precision, and
     * the difference is visible: a fixed decimal prints `14.0 MB` for a banner set,
     * which reads as a measurement nobody took. Bytes are always whole — there is no
     * such thing as 512.5 of them.
     */
    const digits = unit > 0 && value < 10 ? 1 : 0;
    return `${formatNumber(value, locale, { fractionDigits: digits })} ${BYTE_UNITS[unit] ?? 'bytes'}`;
}
/**
 * `settled` → `Settled`. The first letter, and nothing else touched.
 *
 * A **rendering** rather than a transformation: the values that go through it are
 * closed sets a schema publishes in lower case — an invoice status, an expense state,
 * a charge state, a content-type noun — and the mockups print them capitalised at the
 * start of a cell or a sentence. It is not `toUpperCase()` on a word a person typed.
 *
 * Here rather than in either portal because both had a copy. `components/client/
 * unitNoun.ts` had the original and session 20 added two more, in two worktrees, for
 * exactly the same reason with exactly the same body.
 */
export function sentenceCase(value) {
    return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}
/* ------------------------------------------------------------------ an article */
/**
 * The indefinite article a noun takes: `a quote`, `an extra`.
 *
 * Session 24 (R3-20). Four outbox rows read `REQ-118 — a extra is waiting on you`,
 * because `approval-requested.ts` wrote `` `a ${noun}` `` and one of the four nouns
 * `DECISION_KIND_WORDS` publishes begins with a vowel. `a quote`, `a package change`
 * and `a content plan` were all correct, which is why nobody saw it until the fourth
 * one was sent.
 *
 * A **rule** rather than the article written into `DECISION_KIND_WORDS` beside each
 * noun, because the rule is the thing that cannot be forgotten: a fifth decision kind
 * gets its article from the same function the other four use, and the only way to
 * reintroduce the defect is to stop calling it. The word next to it is what the
 * session that added `extra` would have got wrong in exactly the same way.
 *
 * The five vowel letters and nothing cleverer. English decides on the *sound* — "an
 * hour", "a university" — and no noun this portal says out loud is one of those; a
 * pronunciation dictionary for a closed list of four product words would be a lot of
 * machinery to get the same four answers. If one ever arrives, this is the one place
 * it is handled.
 */
export function articleFor(noun) {
    return /^[aeiou]/i.test(noun.trim()) ? 'an' : 'a';
}
/** `a quote`, `an extra` — the noun with the article it takes. */
export function withArticle(noun) {
    return `${articleFor(noun)} ${noun}`;
}
/* --------------------------------------------------------------------- a list */
/**
 * `['a']` → `a`, `['a', 'b']` → `a and b`, `['a', 'b', 'c']` → `a, b and c`.
 *
 * Session 19 wrote this three times — the projects page's "Milestones 2 and 3", the
 * billing composer's "Business consulting and Odoo management", the settings page's
 * list of notification kinds — each with a different answer for the empty list.
 * There is one answer here and it is the empty string; a caller that wants a word
 * for "nothing" says which word, because "it" and "nobody" are not interchangeable.
 *
 * No `Intl.ListFormat`: the SPA is the only consumer, `en-GB` is the only locale,
 * and the serial comma is a house decision rather than a locale's.
 */
export function formatList(items) {
    if (items.length === 0)
        return '';
    if (items.length === 1)
        return items[0] ?? '';
    const last = items[items.length - 1] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${last}`;
}
//# sourceMappingURL=format.js.map